// Single source of truth for "how far along is this project".
//
// The `proyectos.progreso` column is legacy: it was only ever written once, by
// the old slider in the "Nuevo Proyecto" modal, and nothing kept it in sync
// afterwards. Progress is always derived from the project's `tareas` instead.

// Average the `progreso` of a flat list of tareas rows into per-project
// buckets, split by `tipo` so callers can render diseño / obra separately.
// Rows only need `proyecto_id`, `progreso` and `tipo`.
// Returns: { [proyecto_id]: { diseno, obra, all } } — each value is an average
// in 0–100, or null when that half has no tasks.
export function buildTaskProgressMap(rows) {
  const buckets = {}; // pid → { sumD, cntD, sumO, cntO, sumA, cntA }
  (rows || []).forEach(t => {
    if (!t || !t.proyecto_id) return;
    const v = Number(t.progreso) || 0;
    const b = buckets[t.proyecto_id] || (buckets[t.proyecto_id] = { sumD: 0, cntD: 0, sumO: 0, cntO: 0, sumA: 0, cntA: 0 });
    const tipo = (t.tipo || '').toLowerCase();
    if (tipo === 'diseño') { b.sumD += v; b.cntD += 1; }
    else if (tipo === 'obra') { b.sumO += v; b.cntO += 1; }
    b.sumA += v; b.cntA += 1;
  });
  const map = {};
  for (const pid of Object.keys(buckets)) {
    const b = buckets[pid];
    map[pid] = {
      diseno: b.cntD ? b.sumD / b.cntD : null,
      obra:   b.cntO ? b.sumO / b.cntO : null,
      all:    b.cntA ? b.sumA / b.cntA : null,
    };
  }
  return map;
}

// Per-project progress split by gantt phase. Returns { total, disenoPct,
// obraPct, hasDiseno, hasObra }. Total respects the project's `modalidad`:
//   'Diseño' → diseño avg
//   'Obra'   → obra avg
//   'Ambos'  → mean of the two (only counting halves that have tasks)
export function getProjectProgressBreakdown(project, taskProgressMap) {
  const empty = { total: 0, disenoPct: null, obraPct: null, hasDiseno: false, hasObra: false };
  if (!project) return empty;
  const split = (taskProgressMap || {})[project.id];
  const dis = split && Number.isFinite(split.diseno) ? Math.round(split.diseno) : null;
  const obr = split && Number.isFinite(split.obra) ? Math.round(split.obra) : null;
  const hasDiseno = dis != null;
  const hasObra = obr != null;
  const modalidad = (project.modalidad || '').trim();
  let total;
  if (modalidad === 'Diseño') {
    total = hasDiseno ? dis : 0;
  } else if (modalidad === 'Obra') {
    total = hasObra ? obr : 0;
  } else if (modalidad === 'Ambos') {
    if (hasDiseno && hasObra) total = Math.round((dis + obr) / 2);
    else if (hasDiseno) total = dis;
    else if (hasObra) total = obr;
    else total = 0;
  } else {
    // Modalidad unset — fall back to whatever data exists.
    if (hasDiseno && hasObra) total = Math.round((dis + obr) / 2);
    else if (hasDiseno) total = dis;
    else if (hasObra) total = obr;
    else if (split && Number.isFinite(split.all)) total = Math.round(split.all);
    else total = 0;
  }
  return {
    total: Math.max(0, Math.min(100, total)),
    disenoPct: hasDiseno ? Math.max(0, Math.min(100, dis)) : null,
    obraPct: hasObra ? Math.max(0, Math.min(100, obr)) : null,
    hasDiseno,
    hasObra,
  };
}
